// Continuous dual-handle ONI anomaly range slider (-3 .. +3, step 0.1).
// Mirrors the Streamlit control: presets set the range, the slider allows any
// custom band, and a dynamic regime label describes the current selection.

const MIN = -3;
const MAX = 3;
const STEP = 0.1;

interface Props {
  value: [number, number];
  onChange: (v: [number, number]) => void;
}

function regime(lo: number, hi: number): { label: string; color: string } {
  if (lo === MIN && hi === MAX) return { label: "All regimes", color: "rgba(255,255,255,0.5)" };
  if (lo <= -0.5 && hi <= -0.5) return { label: "La Niña only", color: "#60A5FA" };
  if (lo >= 0.5 && hi >= 0.5) return { label: "El Niño only", color: "#F97316" };
  if (lo >= -0.5 && hi <= 0.5) return { label: "Neutral only", color: "#A78BFA" };
  const f = (n: number) => `${n >= 0 ? "+" : ""}${n.toFixed(1)}`;
  return { label: `Custom: ${f(lo)} to ${f(hi)}`, color: "rgba(255,255,255,0.7)" };
}

const round1 = (n: number) => Math.round(n * 10) / 10;
const pct = (v: number) => ((v - MIN) / (MAX - MIN)) * 100;

export default function OniRangeSlider({ value, onChange }: Props) {
  const [lo, hi] = value;
  const r = regime(lo, hi);

  const setLo = (v: number) => onChange([Math.min(round1(v), hi), hi]);
  const setHi = (v: number) => onChange([lo, Math.max(round1(v), lo)]);

  return (
    <div className="oni-slider">
      <div className="dual">
        <div className="dual-track" />
        <div
          className="dual-range"
          style={{ left: `${pct(lo)}%`, right: `${100 - pct(hi)}%` }}
        />
        <input
          type="range" className="thumb thumb-lo"
          min={MIN} max={MAX} step={STEP} value={lo}
          onChange={(e) => setLo(Number(e.target.value))}
          aria-label="ONI minimum"
        />
        <input
          type="range" className="thumb thumb-hi"
          min={MIN} max={MAX} step={STEP} value={hi}
          onChange={(e) => setHi(Number(e.target.value))}
          aria-label="ONI maximum"
        />
      </div>
      <div className="oni-readout">
        <span style={{ color: r.color, fontWeight: 600 }}>{r.label}</span>
        <span className="muted">
          {lo >= 0 ? "+" : ""}{lo.toFixed(1)} … {hi >= 0 ? "+" : ""}{hi.toFixed(1)}
        </span>
      </div>
    </div>
  );
}
